<template>
    <section class="flex flex-col text-base">
        <PageHeader wide>
            <h1>{{ $t("Contact.Page_Title") }}</h1>
        </PageHeader>

        <div class="container-left max-w-5xl flex flex-col gap-4 mt-8">
            <Alert v-if="formState === 'error'" variant="danger" class="mb-4">
                <AlertDescription>
                    <p v-html="$t('Contact.SubmissionError')" />
                </AlertDescription>
            </Alert>

            <Alert v-if="formState === 'success'" variant="success" class="mb-4">
                <AlertDescription>
                    <p v-html="$t('Contact.FeedbackHtml')" />
                </AlertDescription>
            </Alert>

            <template v-else>
                <div v-html="$t('Contact.NormalHtml')"></div>

                <form
                    role="form"
                    class="flex flex-col gap-4 w-full sm:w-1/2 sm:ml-40"
                    novalidate
                    @submit.stop.prevent="onEditSubmit">
                    <ContactUsField
                        v-model="contactDetails.Name"
                        :name="$t('Global.Name')"
                        :placeholder="$t('Contact.EnterName')"
                        :required="true"
                        :error="issueMessages.Name"
                        @blur="onBlur('Name')" />

                    <ContactUsField
                        v-model="contactDetails.EmailAddress"
                        :name="$t('Global.Email')"
                        :placeholder="$t('Contact.EnterEmail')"
                        :required="true"
                        :error="issueMessages.EmailAddress"
                        @blur="onBlur('EmailAddress')" />

                    <ContactUsField
                        v-model="contactDetails.PhoneNumber"
                        :name="$t('Contact.Phone')"
                        :placeholder="$t('Contact.EnterPhone')"
                        :error="issueMessages.PhoneNumber"
                        @blur="onBlur('PhoneNumber')" />

                    <ContactUsField
                        v-model="contactDetails.Comments"
                        :name="$t('Contact.Comments')"
                        :placeholder="$t('Contact.Comments')"
                        :required="true"
                        :error="issueMessages.Comments"
                        @blur="onBlur('Comments')" />

                    <div>
                        <Button type="submit" variant="primary" fill="solid" size="lg" :disabled="isFormDisabled">
                            <Spinner v-if="isBusy" size="xs" variant="white" />
                            <span>{{ $t("Global.Submit") }}</span>
                        </Button>
                    </div>
                </form>
            </template>

            <p v-html="moreInformationMessage"></p>
        </div>
    </section>
</template>
<script setup lang="ts">
import * as v from "valibot";

const { t } = useI18n();
const { $api } = useNuxtApp();
const config = useRuntimeConfig();

useSeoMeta({
    title: t("Contact.Page_Title"),
    ogTitle: t("Contact.Page_Title"),
    description: t("Contact.MetaDescription"),
    ogDescription: t("Contact.MetaDescription"),
});

const eciaUrlLink = '<a href="https://www.ecianow.org">ecianow.org</a>';
const feedbackLink = `<a href="mailto:${config.public.feedbackEmail}">${config.public.feedbackEmail}</a>`;
const ContactSchema = v.object({
    Name: v.pipe(v.string(), v.trim(), v.nonEmpty(t("Contact.NameRequired"))),
    EmailAddress: v.pipe(
        v.string(),
        v.trim(),
        v.email(t("MyAccount.ErrorEmailIsInvalid")),
        v.nonEmpty(t("Global.EmailRequired"))
    ),
    // same pattern as the server checks
    PhoneNumber: v.union([
        v.pipe(v.string(), v.trim(), v.literal("")),
        v.pipe(
            v.string(),
            v.trim(),
            v.regex(
                /^\s*(?:\+?(\d{1,3})\/?)?([( ]*0[) ]*)?([-. (]*(\d{1,3})[-. )]*)?((\d{3})[-. ]*(\d{2,4})(?:[-.x ext]*(\d{0,8}))?)\s*$/,
                t("Contact.PhoneError")
            )
        ),
    ]),
    Comments: v.pipe(v.string(), v.trim(), v.nonEmpty(t("Contact.CommentsRequired"))),
});

const formState = ref<"initial" | "error" | "success">("initial");
const contactDetails = ref({
    Name: "",
    EmailAddress: "",
    PhoneNumber: "",
    Comments: "",
});
const touchedFields = ref<{ [key: string]: boolean }>({
    Name: false,
    EmailAddress: false,
    PhoneNumber: false,
    Comments: false,
});
const isBusy = ref(false);

const parseResult = computed(() => v.safeParse(ContactSchema, contactDetails.value));
const issueMessages = computed(() =>
    Object.fromEntries(
        parseResult.value?.issues?.map((i) => {
            const field = v.getDotPath(i) || "";
            return touchedFields.value[field] ? [field, i.message] : [];
        }) ?? []
    )
);
const moreInformationMessage = computed(() => t("Contact.MoreInformation", [eciaUrlLink, feedbackLink]));
const isFormDisabled = computed(() => isBusy.value || !parseResult.value?.success);

function onBlur(field: string) {
    touchedFields.value[field] = true;
}

async function onEditSubmit() {
    formState.value = "initial";

    if (isFormDisabled.value || !parseResult.value) return;

    try {
        isBusy.value = true;

        const response = await $api<{ Complete: boolean }>("contact-us", {
            method: "POST",
            body: parseResult.value.output as object,
        });

        formState.value = response.Complete ? "success" : "error";
    } catch (err) {
        formState.value = "error";
        console.error(err);
    } finally {
        isBusy.value = false;
    }
}
</script>
<style>
@reference "../assets/css/main.css";
.lead-text {
    @apply font-bold text-2xl mb-6;
}
</style>
